// Authentication Context
// Manages user authentication state and provides auth methods throughout the app

import React, { createContext, useState, useEffect, ReactNode } from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
    updateProfile,
    User as FirebaseUser,
    IdTokenResult
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { auth, db } from '@lib/firebase';

import { USER_ROLES, UserRole, validators } from '@/shared/constants';

// Type definitions
import { AuthContext as AuthContextImport, AuthContextValue, UserProfile } from './AuthContextObject';
const AuthContext = AuthContextImport;
export { AuthContext };

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<FirebaseUser | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
    const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);

    // Apply theme based on preference
    const applyTheme = (isDarkMode: boolean): void => {
        // Force light mode always as dark mode is temporarily disabled
        document.body.classList.remove('dark-mode');
        document.body.classList.add('light-mode');
    };

    const createFallbackProfile = (uid: string, email: string, displayName: string): UserProfile => {
        return {
            uid,
            email,
            displayName: displayName || '',
            role: USER_ROLES.STUDENT,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };
    };

    const extractJWTClaims = async (firebaseUser: FirebaseUser): Promise<{ role: string; source: string } | null> => {
        if (!firebaseUser) return null;
        console.log(`[AuthContext] Starting JWT claim extraction for ${firebaseUser.uid}`);
        try {
            // Add a timeout to JWT extraction to prevent hanging in emulator mode
            const tokenPromise = firebaseUser.getIdTokenResult(true);
            const timeoutPromise = new Promise<null>((_, reject) =>
                setTimeout(() => reject(new Error('JWT Extraction Timeout')), 5000)
            );

            const tokenResult = await Promise.race([tokenPromise, timeoutPromise]) as IdTokenResult;
            const roleFromClaim = tokenResult.claims?.role as string | undefined;

            if (roleFromClaim) {
                console.log(`[AuthContext] Found JWT role: ${roleFromClaim} for ${firebaseUser.uid}`);
                return { role: roleFromClaim, source: 'JWT' };
            }
            console.log(`[AuthContext] No JWT role found for ${firebaseUser.uid}`);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            console.warn(`[AuthContext] Failed to extract JWT claims for ${firebaseUser.uid}:`, errorMessage);
        }
        return null;
    };

    const fetchUserProfile = async (uid: string, email: string = '', displayName: string = ''): Promise<UserProfile | null> => {
        const _startTime = performance.now();
        try {
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Firestore fetch timeout')), 10000)
            );

            const fetchPromise = getDoc(doc(db, 'users', uid));
            const userDoc = await Promise.race([fetchPromise, timeoutPromise]);

            const _fetchTime = performance.now() - _startTime;

            if (userDoc.exists()) {
                return userDoc.data() as UserProfile;
            }

            return null;
        } catch (err) {
            const _fetchTime = performance.now() - _startTime;
            const errorMessage = err instanceof Error ? err.message : String(err);
            console.warn(`Profile fetch failed for ${uid} after ${_fetchTime.toFixed(2)}ms:`, errorMessage);
            return createFallbackProfile(uid, email, displayName);
        }
    };

    // Create user profile in Firestore
    const createUserProfile = async (uid: string, data: RegistrationData): Promise<UserProfile> => {
        try {
            console.warn('DEBUG: createUserProfile start', uid);
            const userRef = doc(db, 'users', uid);

            const age = validators.calculateAge(data.dateOfBirth || '');
            const isMinor = age !== null && age < 18;

            const profileData: UserProfile = {
                uid,
                email: data.email || '',
                displayName: data.displayName || '',
                role: data.role || USER_ROLES.STUDENT,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isMinor,
                ...data
            };

            if (isMinor && data.parentGuardian?.email) {
                profileData.parentConsent = {
                    status: 'pending',
                    parentEmail: data.parentGuardian.email,
                    parentName: null,
                    token: null,
                    tokenCreatedAt: null,
                    signedAt: null
                };
            }

            console.warn('DEBUG: setDoc calling...');
            await setDoc(userRef, profileData);
            console.warn('DEBUG: setDoc complete');
            return profileData;
        } catch (err) {
            console.error('Error creating user profile:', err);
            throw err;
        }
    };

    // Listen to auth state changes and initialize profile with real-time sync
    useEffect(() => {
        let unsubscribeSnapshot: Unsubscribe | null = null;
        const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
            let isInitialSnapshot = true;
            console.log('[AuthContext] onAuthStateChanged:', firebaseUser ? `User ${firebaseUser.uid}` : 'NULL');

            // Clean up existing snapshot listener if any
            if (unsubscribeSnapshot) {
                unsubscribeSnapshot();
                unsubscribeSnapshot = null;
            }

            try {
                if (firebaseUser) {
                    setLoading(true);
                    setUser(firebaseUser);
                    console.log(`[AuthContext] Authenticated: ${firebaseUser.uid}. Initializing profile...`);

                    // Safety timeout to force loading=false if Snapshot/JWT takes too long
                    const safetyTimeout = setTimeout(() => {
                        if (isInitialSnapshot) {
                            console.warn('[AuthContext] SAFETY TIMEOUT REACHED. Forcing loading=false');
                            isInitialSnapshot = false;
                            setLoading(false);
                        }
                    }, 8000);

                    const jwtClaim = await extractJWTClaims(firebaseUser);
                    console.log(`[AuthContext] Setting up profile listener for ${firebaseUser.uid}`);

                    // Setup real-time profile listener
                    unsubscribeSnapshot = onSnapshot(
                        doc(db, 'users', firebaseUser.uid),
                        (docSnapshot) => {
                            clearTimeout(safetyTimeout);
                            console.log(`[AuthContext] Profile snapshot received for ${firebaseUser.uid}. Exists: ${docSnapshot.exists()}`);
                            if (docSnapshot.exists()) {
                                const profile = docSnapshot.data() as UserProfile;
                                const snapshotProfile: UserProfile = {
                                    ...profile,
                                    role: jwtClaim?.role || profile.role
                                };
                                setUserProfile(snapshotProfile);

                                if (snapshotProfile?.requiresPasswordChange) {
                                    setRequiresPasswordChange(true);
                                    setShowPasswordChangeModal(true);
                                } else {
                                    setRequiresPasswordChange(false);
                                    setShowPasswordChangeModal(false);
                                }

                                const settings = snapshotProfile?.settings || {
                                    darkMode: false,
                                    notifications: true,
                                    emailNotifications: true
                                };
                                applyTheme(settings.darkMode || false);
                            } else {
                                // Handle missing profile with fallback
                                console.warn('[AuthContext] Profile missing in snapshot, using fallback');
                                const fallback = createFallbackProfile(
                                    firebaseUser.uid,
                                    firebaseUser.email || '',
                                    firebaseUser.displayName || ''
                                );
                                if (jwtClaim?.role) fallback.role = jwtClaim.role;
                                setUserProfile(fallback);
                            }

                            if (isInitialSnapshot) {
                                console.log('[AuthContext] Initial loading complete via snapshot');
                                isInitialSnapshot = false;
                                setLoading(false);
                            }
                        },
                        (err) => {
                            clearTimeout(safetyTimeout);
                            console.warn('[AuthContext] Snapshot listener error:', err);
                            // Even on error, we must eventually stop loading
                            if (isInitialSnapshot) {
                                isInitialSnapshot = false;
                                setLoading(false);
                            }
                        }
                    );
                } else {
                    console.log('[AuthContext] User logged out, clearing state');
                    setUser(null);
                    setUserProfile(null);
                    setRequiresPasswordChange(false);
                    setShowPasswordChangeModal(false);
                    applyTheme(false);
                    setLoading(false);
                }
            } catch (err) {
                console.error('[AuthContext] Auth initialization error:', err);
                setError(err as Error);
                setLoading(false);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeSnapshot) {
                unsubscribeSnapshot();
            }
        };
    }, []);

    // Login with email and password
    const login = async (email: string, password: string): Promise<FirebaseUser> => {
        setLoading(true);
        try {
            setError(null);
            const result = await signInWithEmailAndPassword(auth, email, password);
            await extractJWTClaims(result.user);

            // Explicitly set user state to prevent race condition with onAuthStateChanged
            // causing ProtectedRoute to redirect before state updates
            setUser(result.user);

            return result.user;
        } catch (err) {
            setError(err as Error);
            throw err;
        }
    };

    // Register new user
    const register = async (email: string, password: string, additionalData: RegistrationData = {}): Promise<FirebaseUser> => {
        try {
            setError(null);

            // Validate required fields for DTO 0201/0051 compliance
            const requiredFields = ['firstName', 'lastName', 'middleName', 'dateOfBirth', 'address', 'city', 'zipCode'];
            const missingFields = requiredFields.filter(field => !additionalData[field]);

            if (missingFields.length > 0) {
                throw new Error(`Missing required fields for registration: ${missingFields.join(', ')}`);
            }

            // If user is under 18, validate parent/guardian contact information
            const age = validators.calculateAge(additionalData.dateOfBirth || '');
            if (age !== null && age < 18) {
                if (!additionalData.parentGuardian?.email) {
                    throw new Error('Parent/Guardian email is required for users under 18');
                }
                if (!additionalData.parentGuardian?.phone) {
                    throw new Error('Parent/Guardian phone is required for users under 18');
                }
            }

            console.warn('DEBUG: Calling createUserWithEmailAndPassword', { email });
            const result = await createUserWithEmailAndPassword(auth, email, password);
            console.warn('DEBUG: Auth successful, uid:', result.user.uid);

            // Update display name if provided
            if (additionalData.displayName) {
                try {
                    await updateProfile(result.user, {
                        displayName: additionalData.displayName
                    });
                } catch (profileError) {
                    console.warn('DEBUG: Failed to update auth profile (non-critical):', profileError);
                }
            }

            // Create user profile in Firestore and await completion
            console.warn('DEBUG: Creating Firestore profile for', result.user.uid);
            const profileData = await createUserProfile(result.user.uid, {
                email,
                displayName: additionalData.displayName || '',
                ...additionalData
            });
            console.warn('DEBUG: Profile created successfully');

            // Send verification email before setting state
            console.warn('DEBUG: Sending verification email');
            try {
                await sendEmailVerification(result.user);
                console.warn('DEBUG: Verification email sent successfully');
            } catch (emailError) {
                console.warn('DEBUG: Failed to send verification email:', emailError);
            }

            // Explicitly set user and profile state to prevent race condition with onAuthStateChanged
            setUser(result.user);
            setUserProfile(profileData);
            setLoading(false);

            return result.user;
        } catch (err) {
            console.error('DEBUG: Register error:', err);
            // If profile creation fails, delete the Auth user to prevent orphaned accounts
            if (auth.currentUser) {
                await auth.currentUser.delete();
            }
            setError(err as Error);
            throw err;
        }
    };

    // Logout
    const logout = async (): Promise<void> => {
        try {
            setError(null);
            await signOut(auth);
            setUser(null);
            setUserProfile(null);
        } catch (err) {
            setError(err as Error);
            throw err;
        }
    };

    // Reset password
    const resetPassword = async (email: string): Promise<void> => {
        try {
            setError(null);
            await sendPasswordResetEmail(auth, email);
        } catch (err) {
            setError(err as Error);
            throw err;
        }
    };

    // Update user profile
    const updateUserProfile = async (updates: ProfileUpdates): Promise<UserProfile | null> => {
        try {
            setError(null);
            if (!user) throw new Error('No user logged in');

            // Update Firebase Auth profile if display name or photo URL changed
            if (updates.displayName || updates.photoURL) {
                await updateProfile(user, {
                    displayName: updates.displayName || user.displayName || undefined,
                    photoURL: updates.photoURL || user.photoURL || undefined
                });
            }

            // Update Firestore profile and refresh asynchronously
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
                ...updates,
                updatedAt: new Date().toISOString()
            });

            // Refresh profile in background
            fetchUserProfile(
                user.uid,
                user.email || '',
                user.displayName || ''
            ).then(updatedProfile => {
                if (updatedProfile) {
                    setUserProfile(updatedProfile);
                }
            }).catch(err => console.warn('Profile refresh failed:', err));

            // Return current profile immediately
            return userProfile;
        } catch (err) {
            setError(err as Error);
            throw err;
        }
    };

    // Resend verification email with cooldown timer (60 seconds)
    const resendVerificationEmail = async (): Promise<void> => {
        try {
            setError(null);
            const currentUser = auth.currentUser;

            if (!currentUser) {
                throw new Error('No user logged in');
            }

            // Check cooldown timer from localStorage
            const lastEmailSentTime = localStorage.getItem(`verificationEmailSent_${currentUser.uid}`);
            const now = Date.now();
            const COOLDOWN_MS = 60000; // 60 seconds

            if (lastEmailSentTime) {
                const timeSinceLastEmail = now - parseInt(lastEmailSentTime, 10);
                if (timeSinceLastEmail < COOLDOWN_MS) {
                    const secondsRemaining = Math.ceil((COOLDOWN_MS - timeSinceLastEmail) / 1000);
                    throw new Error(`Please wait ${secondsRemaining} seconds before requesting another verification email`);
                }
            }

            // Send verification email
            await sendEmailVerification(currentUser);

            // Store timestamp of this email send
            localStorage.setItem(`verificationEmailSent_${currentUser.uid}`, now.toString());
        } catch (err) {
            setError(err as Error);
            throw err;
        }
    };

    // Check if user has specific role
    const hasRole = (role: UserRole | UserRole[]): boolean => {
        if (!userProfile) return false;
        if (Array.isArray(role)) {
            return role.includes(userProfile.role as UserRole);
        }
        return userProfile.role === role;
    };

    // Get user's full name
    const getUserFullName = (): string => {
        if (!userProfile) return '';
        return userProfile.displayName || userProfile.email?.split('@')[0] || 'User';
    };

    const value: AuthContextValue = {
        user,
        userProfile,
        loading,
        error,
        requiresPasswordChange,
        showPasswordChangeModal,
        setShowPasswordChangeModal,
        login,
        register,
        logout,
        resetPassword,
        resendVerificationEmail,
        updateUserProfile,
        hasRole,
        getUserFullName,
        isAuthenticated: !!user,
        isAdmin: hasRole([USER_ROLES.DMV_ADMIN, USER_ROLES.SUPER_ADMIN]),
        isInstructor: hasRole(USER_ROLES.INSTRUCTOR),
        isStudent: hasRole(USER_ROLES.STUDENT)
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Export the provider as default for better HMR compatibility with Vite
export default AuthProvider;
